import { z } from 'zod';
/**
 * Sales page content schema.
 *
 * A page's content is a versioned JSON document: `{ version, blocks[] }` where
 * each block is a discriminated union on `type`. The renderer (apps/web) and
 * the editor both consume this schema and neither knows about the other — new
 * block types are added here once and both sides pick them up.
 *
 * `version` lets future releases migrate stored documents without a backfill.
 */
export const SALES_PAGE_SCHEMA_VERSION = 1;
export const BLOCK_TYPES = [
    'hero',
    'heading',
    'text',
    'image',
    'product',
    'order-form',
];
const alignment = z.enum(['left', 'center', 'right']);
const themeVariant = z.enum(['light', 'dark', 'brand']);
const baseBlock = z.object({
    /** Stable client-generated id, used as the React key and for reordering. */
    id: z.string().min(1).max(64),
});
export const heroBlockSchema = baseBlock.extend({
    type: z.literal('hero'),
    props: z.object({
        headline: z.string().min(1).max(200),
        subheadline: z.string().max(500).optional(),
        imageUrl: z.string().url().optional(),
        ctaLabel: z.string().max(60).optional(),
        /** Anchor id of the block to scroll to, e.g. the order form. */
        ctaTarget: z.string().max(64).optional(),
        align: alignment.default('center'),
        variant: themeVariant.default('brand'),
    }),
});
export const headingBlockSchema = baseBlock.extend({
    type: z.literal('heading'),
    props: z.object({
        text: z.string().min(1).max(200),
        level: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(2),
        align: alignment.default('left'),
    }),
});
export const textBlockSchema = baseBlock.extend({
    type: z.literal('text'),
    props: z.object({
        /** Plain text with newlines. Rendered escaped — no raw HTML is accepted. */
        content: z.string().min(1).max(10_000),
        align: alignment.default('left'),
    }),
});
export const imageBlockSchema = baseBlock.extend({
    type: z.literal('image'),
    props: z.object({
        url: z.string().url(),
        alt: z.string().max(200).default(''),
        caption: z.string().max(300).optional(),
        width: z.enum(['full', 'wide', 'narrow']).default('wide'),
    }),
});
export const productBlockSchema = baseBlock.extend({
    type: z.literal('product'),
    props: z.object({
        productId: z.string().uuid(),
        showPrice: z.boolean().default(true),
        showDescription: z.boolean().default(true),
        ctaLabel: z.string().max(60).default('Add to order'),
    }),
});
export const orderFormBlockSchema = baseBlock.extend({
    type: z.literal('order-form'),
    props: z.object({
        title: z.string().max(200).default('Complete your order'),
        /** Products offered in the form. Prices are always re-read server-side. */
        productIds: z.array(z.string().uuid()).min(1).max(20),
        collectPhone: z.boolean().default(true),
        collectShippingAddress: z.boolean().default(true),
        allowCoupon: z.boolean().default(true),
        submitLabel: z.string().max(60).default('Place order'),
    }),
});
export const salesPageBlockSchema = z.discriminatedUnion('type', [
    heroBlockSchema,
    headingBlockSchema,
    textBlockSchema,
    imageBlockSchema,
    productBlockSchema,
    orderFormBlockSchema,
]);
export const salesPageContentSchema = z.object({
    version: z.literal(SALES_PAGE_SCHEMA_VERSION),
    blocks: z.array(salesPageBlockSchema).max(100),
    settings: z
        .object({
        backgroundColor: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex colour such as #0f172a')
            .optional(),
        maxWidth: z.enum(['narrow', 'default', 'wide']).default('default'),
    })
        .default({ maxWidth: 'default' }),
});
/** Content used for a brand-new page. */
export function emptySalesPageContent() {
    return {
        version: SALES_PAGE_SCHEMA_VERSION,
        blocks: [],
        settings: { maxWidth: 'default' },
    };
}
/**
 * Parse untrusted JSON (from the DB or a request body) into typed content.
 * Throws a ZodError the caller converts into a 422.
 */
export function parseSalesPageContent(input) {
    return salesPageContentSchema.parse(input);
}
/** Every product referenced anywhere on the page, de-duplicated. */
export function collectReferencedProductIds(content) {
    const ids = new Set();
    for (const block of content.blocks) {
        if (block.type === 'product') {
            ids.add(block.props.productId);
        }
        else if (block.type === 'order-form') {
            for (const id of block.props.productIds)
                ids.add(id);
        }
    }
    return [...ids];
}
/** Human-readable labels for the block palette in the editor. */
export const BLOCK_LABELS = {
    hero: 'Hero banner',
    heading: 'Heading',
    text: 'Text paragraph',
    image: 'Image',
    product: 'Product card',
    'order-form': 'Order form',
};
//# sourceMappingURL=blocks.js.map